import {
  Badge,
  Box,
  Button,
  Drawer,
  Flex,
  LinkBox,
  LinkOverlay,
  Spinner,
  VStack,
  useDisclosure,
  useDrawerContext
} from "@chakra-ui/react"
import { useObserveSizeContext } from "@pathwright/ui/src/components/observers/ObserveSizeContext"
import { flattenEdges } from "@pathwright/utils"
import { useIsScrolled } from "@pathwright/utils/src/scroll/useIsScrolled"
import UserAvatar from "@pathwright/ux/src/components/avatar/UserAvatar"
import MenuButton from "@pathwright/ux/src/components/button/MenuButton"
import CardLink from "@pathwright/ux/src/components/card/CardLink"
import CardLinks from "@pathwright/ux/src/components/card/CardLinks"
import Link from "@pathwright/ux/src/components/link/Link"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { motion } from "framer-motion"
import { usePagesQuery } from "@pathwright/graphql-client/src/generated"
import AdaptedSignedInContainer from "../../auth/AdaptedSignedInContainer"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import PWAdminHint from "../../pw-admin/PWAdminHint"
import { useShowSubscribePrompt } from "../../subscription/useShowSubscribePrompt"
import { useCanViewSimpleNav } from "../../unified-home/view-permission/useUnifiedHomePermissions"
import { canAdministrate } from "../../user/permissions"
import SpaceIdentity from "../identity/SpaceIdentity"
import SpaceSwitcher from "../switcher/SpaceSwitcher"
import SpaceActivationPrompt from "./SpaceActivationPrompt"

// Hide space augmentation drawer identity.
export const spaceAugmentationDrawerIdentityHideCss = {
  "body:has(&) .SpaceAugmentationDrawer-Identity": {
    display: "none"
  }
}

function SpaceSwitcherContent() {
  const pwContext = usePathwrightContext()
  const { me } = pwContext

  const { t, tc } = useTranslate()
  const showSubscribe = useShowSubscribePrompt()
  const [isScrolled, setContainerRef] = useIsScrolled()

  const pagesQuery = usePagesQuery({
    variables: { first: 25, isLive: true, showInNav: true }
  })
  const pages = flattenEdges(pagesQuery.data?.school?.pages)

  const drawer = useDrawerContext()
  const onClose = () => drawer.setOpen(false)

  // Await any fetched data before rendering content.
  const loading = pagesQuery.loading

  return (
    <VStack ref={setContainerRef} overflow="auto" align="start" p={2} h="100%">
      <Box
        w="100%"
        pos="sticky"
        top={0}
        zIndex={1}
        bg={isScrolled ? "whiteAlpha.900" : "transparent"}
        transition="background-color 200ms ease-in-out"
        borderRadius="card"
      >
        <SpaceSwitcher />
      </Box>

      {loading ? (
        <Flex flexGrow={1} alignItems="center" justifyContent="center" w="100%">
          <Spinner />
        </Flex>
      ) : (
        <>
          <CardLinks>
            {me && (
              <Box w="100%">
                <CardLink
                  to="/home/"
                  onClick={onClose}
                  label={t("Home")}
                  renderIcon={() => <UserAvatar user={me} size="2xs" />}
                />
              </Box>
            )}
            <CardLink
              to="/library/"
              onClick={onClose}
              label={t("library.store_labels.library")}
              icon="book"
            />
            {showSubscribe && (
              <CardLink
                to="/subscribe/"
                onClick={onClose}
                label={t("Subscribe")}
                icon="credit-card"
              />
            )}
            {pages?.map((page) => (
              <PWAdminHint key={page.id} model={page} triggerAsChild>
                <CardLink
                  to={page.url.startsWith("/") ? page.url : undefined}
                  href={page.url.startsWith("/") ? undefined : page.url}
                  onClick={onClose}
                  label={page.name}
                  icon="newspaper"
                />
              </PWAdminHint>
            ))}
            <CardLink
              to="/help/"
              onClick={onClose}
              label={t("nav.app_panel_links.need_help")}
              icon="question-mark"
            />
          </CardLinks>

          {canAdministrate(pwContext) && (
            <VStack
              bg="whiteAlpha.500"
              borderRadius="card"
              p={2}
              w="100%"
              align="start"
            >
              <Badge colorPalette="blackAlpha" mx={2} mt={1}>
                {tc("role_labels.noun.school_admin")}
              </Badge>
              <CardLinks>
                <CardLink
                  to="/dashboard/"
                  onClick={onClose}
                  label={t("Dashboard")}
                  icon="gauge-square"
                />
                <CardLink
                  to="/manage/school/"
                  onClick={onClose}
                  label={t("nav.app_panel_links.space_settings")}
                  icon="gear"
                />
                <CardLink
                  to="/community/members/"
                  onClick={onClose}
                  label={t("nav.app_panel_links.staff_members")}
                  icon="multi-user"
                />
                {!!window.Intercom && (
                  <CardLink
                    onClick={() => window.Intercom?.("showNewMessage")}
                    label={t("nav.app_panel_links.pathwright_help")}
                    icon="question-mark"
                  />
                )}
              </CardLinks>
              <SpaceActivationPrompt onClick={onClose} />
            </VStack>
          )}
        </>
      )}
      <Box mt="auto" w="100%" pos="sticky" zIndex={1} bottom={0}>
        {me ? (
          <LinkBox
            bg="whiteAlpha.800"
            borderRadius="card"
            w="100%"
            css={{ "& .AuthWrapper": { px: 4, py: 2 } }}
          >
            <AdaptedSignedInContainer size="xl" />
            <LinkOverlay asChild>
              <Link
                to="/home"
                w="100%"
                onClick={onClose}
                display="flex"
                variant="plain"
              />
            </LinkOverlay>
          </LinkBox>
        ) : (
          <Button asChild colorPalette="brand" variant="solid" width="stretch">
            <Link to="/auth/sign-in/" onClick={onClose}>
              <Pathicon icon="avatar-circle" />
              {t("nav.navbar_user.sign_in")}
              <Pathicon icon="chevron-right" />
            </Link>
          </Button>
        )}
      </Box>
    </VStack>
  )
}

function InnerSpaceAugmentationDrawer() {
  const pwContext = usePathwrightContext()
  const { school } = pwContext
  const disclosure = useDisclosure()

  const { getRectValue } = useObserveSizeContext()
  const alertHeight = getRectValue("alert.height")

  return (
    <Drawer.Root
      open={disclosure.open}
      onOpenChange={(e: { open: boolean }) => !e.open && disclosure.onClose()}
      placement="start"
      size="sm"
    >
      <Drawer.Backdrop bg="blackAlpha.400" />
      <Drawer.ActionTrigger asChild>
        <MenuButton
          className="SpaceAugmentationDrawer-Identity"
          pos="fixed"
          top={`calc(1rem + ${alertHeight || 0}px)`}
          left="1rem"
          zIndex="overlay"
          onClick={disclosure.onOpen}
        >
          <SpaceIdentity space={school} />
        </MenuButton>
      </Drawer.ActionTrigger>
      <Drawer.Positioner asChild padding=".5rem">
        <motion.div
          // Fading in from top left (matches assumed animation from UI with down arrow).
          // TODO: Chakra v3 doesn't support framer motion props and this approach isn't changing actual animation.
          initial={{ opacity: 0, x: -20, y: -20 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          exit={{ opacity: 0, x: -20, y: -20 }}
          transition={{ duration: 0.25 }}
        >
          <Drawer.Content
            bg="whiteAlpha.600"
            backdropFilter="blur(20px)"
            borderRadius="20px"
          >
            <SpaceSwitcherContent />
          </Drawer.Content>
        </motion.div>
      </Drawer.Positioner>
    </Drawer.Root>
  )
}

function SpaceAugmentationDrawer() {
  const canViewUnifiedHome = useCanViewSimpleNav()
  return canViewUnifiedHome ? <InnerSpaceAugmentationDrawer /> : null
}

export default SpaceAugmentationDrawer
